import React from "react";
import {
  // SbDropdown,          // commented out — using direct PDF download instead of dropdown
  SbTooltip,
  SbSelect,
  EditOutlined,
  DownloadOutlined,
  // FilePdfOutlined,     // commented out — dropdown menu items not needed for direct download
  // FileWordOutlined,    // commented out — Word download not implemented yet
  SyncOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  TooltipTriggerStyled,
} from "uiCore";
import { RobotOutlined, WarningOutlined, CheckCircleOutlined } from "@ant-design/icons";

  
import {BorderHorizontalOutlined,SplitCellsOutlined} from '@ant-design/icons';

import Permission from "Components/Permission";
import { PAIC_VIEW_MODES } from "constants/peoplePlusAI.constants";
import {
  DocumentToolbar,
  ToolbarLeft,
  ToolbarMetaBlock,
  ToolbarMetaTopRow,
  VersionHeaderBadge,
  StatusBadge,
  VersionMetaRow,
  VersionMetaItem,
  ToolbarActions,
  ActionIconButton,
  BuildAgentButton,
} from "Pages/PeoplePlusAIV2/shared/DocumentView.styles";

/**
 * Shared document toolbar component for all PAIC document views
 * Replaces: PACJdPrdToolbarV2, PACJdViewToolbarV2, PACPeopleJDToolbarV2
 */
const SbPACDocumentToolbarV2 = ({
  viewMode,
  versionLabel = "JD Version - 1",
  versionOptions = [],
  selectedVersion,
  onVersionChange,
  showVersionDropdown = false,
  showVersionMeta = false,
  modifiedText,
  ownerText,
  documentStatus,
  showCompareAction = true,
  showDiffAction = true,
  showEditAction = true,
  showReviseAction = true,
  showDownloadAction = true,
  showFullscreenAction = false,
  isFullscreen = false,
  onCompareSummary,
  onDiffCheck,
  onEdit,
  onRevise,
  onDownloadPdf,
  onFullscreenToggle,
  // onDownloadWord,      // commented out — Word download not implemented yet
  showPublishAction = true,
  onPublishToggle,
  editTooltip = "Edit",
  editDisabled = false,
  reviseTooltip = "Revise",
  downloadTooltip = "Download PDF",
  publishTooltip,
  versionDropdownTooltip,
  // Agent Builder
  showBuildAgentAction = false,
  buildStatus = null,        // null | "QUEUED" | "BUILDING" | "DONE" | "FAILED" | "TIMED_OUT"
  buildStatusLoading = false,
  buildId = null,
  onBuildAgent,
  onDownloadAgent,
  buildTokenUsage = null,    // { input_tokens, output_tokens, total_cost_usd } — shown when DONE
}) => {
  const fmtK = (n) => ((n || 0) / 1000).toFixed(1);
  // --- Dropdown menu items commented out — using direct PDF download button instead ---
  // const downloadMenuItems = [
  //   {
  //     key: "pdf",
  //     icon: <FilePdfOutlined />,
  //     label: "Download PDF",
  //     onClick: onDownloadPdf,
  //   },
  //   {
  //     key: "word",
  //     icon: <FileWordOutlined />,
  //     label: "Download Word",
  //     onClick: onDownloadWord,
  //     disabled: !onDownloadWord,
  //   },
  // ];

  const hasMetaContent =
    showVersionMeta && (modifiedText || ownerText || documentStatus);

  const isPublished =
    String(documentStatus || "").toUpperCase() === "PUBLISHED";

  const publishButtonLabel = isPublished ? "Unpublish" : "Publish";

  return (
    <DocumentToolbar>
      <ToolbarLeft>
        {showVersionDropdown ? (
          <div style={{ minWidth: 220 }}>
            {versionDropdownTooltip ? (
              <SbTooltip content={versionDropdownTooltip}>
                <TooltipTriggerStyled>
                  <SbSelect
                    value={selectedVersion}
                    options={versionOptions}
                    onChange={onVersionChange}
                    placeholder="Select version"
                    style={{ width: "100%" }}
                  />
                </TooltipTriggerStyled>
              </SbTooltip>
            ) : (
              <SbSelect
                value={selectedVersion}
                options={versionOptions}
                onChange={onVersionChange}
                placeholder="Select version"
                style={{ width: "100%" }}
              />
            )}
          </div>
        ) : (
          <VersionHeaderBadge>{versionLabel}</VersionHeaderBadge>
        )}
        
        {hasMetaContent && (
          <VersionMetaRow>
            {modifiedText && <VersionMetaItem>{modifiedText}</VersionMetaItem>}
            {ownerText && <VersionMetaItem>{ownerText}</VersionMetaItem>}
          </VersionMetaRow>
        )}

      </ToolbarLeft>

      {viewMode !== PAIC_VIEW_MODES.EDIT && (
        <ToolbarActions>
          <Permission resource="people_plus_ai-agent_prd" actions={["build_agent"]}>
          {/* 1. Token usage — only when agent is built */}
          {showBuildAgentAction && buildStatus === "DONE" && buildTokenUsage && (
            <div style={{
              paddingRight: 12,
              borderRight: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <span style={{ fontSize: 11, color: "#5f6368", whiteSpace: "nowrap" }}>
                {fmtK((buildTokenUsage.input_tokens || 0) + (buildTokenUsage.output_tokens || 0) + (buildTokenUsage.cache_read_tokens || 0))}k tokens consumed
              </span>
            </div>
          )}

          {/* 2. Build Agent button */}
          {showBuildAgentAction && buildStatusLoading && (
            <BuildAgentButton disabled $isLoading>
              <RobotOutlined /> Checking…
            </BuildAgentButton>
          )}
          {showBuildAgentAction && !buildStatusLoading && (() => {
            const isRunning = buildStatus === "QUEUED" || buildStatus === "BUILDING";
            const isDone    = buildStatus === "DONE";
            const isFailed  = buildStatus === "FAILED" || buildStatus === "TIMED_OUT";
            const label     = isRunning ? "Building Agent"
                            : isDone    ? "Agent Ready"
                            : isFailed  ? "Retry Build"
                            :             "Build Agent";
            const tooltip   = isRunning ? "Build in progress — click to view progress"
                            : isDone    ? "Agent built — click to download"
                            : isFailed  ? `Build ${buildStatus === "TIMED_OUT" ? "timed out" : "failed"} — click to retry`
                            :             "Build agent from this PRD";
            return (
              <SbTooltip content={tooltip} placement="top">
                <BuildAgentButton
                  $isRunning={isRunning}
                  $isFailed={isFailed}
                  $isDone={isDone}
                  onClick={isDone ? onDownloadAgent : onBuildAgent}
                >
                  <span className={isRunning ? "robot-spin" : ""}>
                    {isFailed
                      ? <WarningOutlined />
                      : isDone
                      ? <CheckCircleOutlined />
                      : <RobotOutlined />}
                  </span>
                  {label}
                </BuildAgentButton>
              </SbTooltip>
            );
          })()}
          </Permission>

          {/* 3. Edit */}
          {showEditAction && onEdit && (
            <SbTooltip content={editTooltip} placement="top">
              <TooltipTriggerStyled>
                <ActionIconButton
                  icon={<EditOutlined />}
                  onClick={editDisabled ? undefined : onEdit}
                  disabled={editDisabled}
                />
              </TooltipTriggerStyled>
            </SbTooltip>
          )}

          {/* 4. Download */}
          {showDownloadAction && onDownloadPdf && (
            <SbTooltip content={downloadTooltip} placement="top">
              <ActionIconButton icon={<DownloadOutlined />} onClick={onDownloadPdf} />
            </SbTooltip>
          )}

          {/* 5. Fullscreen */}
          {showFullscreenAction && onFullscreenToggle && (
            <SbTooltip content={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} placement="top">
              <ActionIconButton
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={onFullscreenToggle}
              />
            </SbTooltip>
          )}

          {/* Compare / Diff / Revise / Publish — currently disabled but kept for future use */}
          {showCompareAction && onCompareSummary && (
            <SbTooltip content="Comparison Summary" placement="top">
              <ActionIconButton icon={<SplitCellsOutlined />} onClick={onCompareSummary} />
            </SbTooltip>
          )}
          {showDiffAction && onDiffCheck && (
            <SbTooltip content="Difference Checker" placement="top">
              <ActionIconButton icon={<BorderHorizontalOutlined />} onClick={onDiffCheck} />
            </SbTooltip>
          )}
          {showReviseAction && onRevise && (
            <SbTooltip content={reviseTooltip} placement="top">
              <ActionIconButton icon={<SyncOutlined />} onClick={onRevise} />
            </SbTooltip>
          )}
          {showPublishAction && onPublishToggle && (
            <SbTooltip content={publishTooltip || publishButtonLabel} placement="top">
              <StatusBadge as="button" type="button" onClick={onPublishToggle}>
                {publishButtonLabel}
              </StatusBadge>
            </SbTooltip>
          )}
        </ToolbarActions>
      )}
    </DocumentToolbar>
  );
};

export default SbPACDocumentToolbarV2;